import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { animate, style, transition, trigger } from "@angular/animations";
import { Observable } from "rxjs";
import { take } from "rxjs/operators";
import { NzModalRef, NzModalService } from "ng-zorro-antd/modal";
import { CaptchaService } from "src/app/services/captcha.service";
import { PwaInstallService } from "src/app/services/pwa-install.service";

/**
 * LoginShellComponent - dumb/presentational component.
 *
 * Owns the split-screen layout, form inputs, password toggle, recover modal,
 * loader overlay and the Turnstile token lifecycle. Knows nothing about
 * AuthService / Firestore / routing - those stay in the parent smart
 * components (admin login.component.ts and cliente login.component.ts).
 *
 * Parallelization: on init we kick off `captcha.getToken()` in the
 * background, so by the time the user clicks the submit button the token is
 * usually already in state. On submit, if the promise is still pending we
 * race it against a 3s timeout and proceed with `null` on timeout (degraded
 * mode - backend IP rate limiter is the fallback).
 */
export interface LoginSubmitPayload {
  user: string;
  pwd?: string;
  captchaToken: string | null;
}

@Component({
  selector: "app-login-shell",
  templateUrl: "./login-shell.component.html",
  styleUrls: ["./login-shell.component.scss"],
  animations: [
    trigger("captionFade", [
      transition(":enter", [
        style({ opacity: 0, transform: "scale(0.9)", filter: "blur(6px)" }),
        animate("380ms cubic-bezier(0.22,1,0.36,1)",
          style({ opacity: 1, transform: "scale(1)", filter: "blur(0)" })),
      ]),
      transition(":leave", [
        animate("380ms cubic-bezier(0.22,1,0.36,1)",
          style({ opacity: 0, transform: "scale(0.9)", filter: "blur(6px)" })),
      ]),
    ]),
  ],
})
export class LoginShellComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  /** `admin` = password field visible; `cliente` = email-only link flow. */
  @Input() mode: "admin" | "cliente" = "admin";
  @Input() loading = false;
  @Input() error: string | null = null;

  /** Cliente-only: when true, shell renders the success panel instead of the form. */
  @Input() success = false;
  @Input() successTitle: string | null = null;
  @Input() successSubtitle: string | null = null;

  /** Whether to show the "Recupera password" link. Cliente uses magic-link, so no recover. */
  @Input() showRecover = true;

  /** Start directly in recover view (for /recupera route). */
  @Input() startInRecover = false;

  /** Recover result: parent sets these after the API call completes. */
  @Input() recoverSuccess = false;
  @Input() recoverError: string | null = null;

  /** Reset password view (for /recupera/:code). */
  @Input() resetView = false;
  @Input() resetError: string | null = null;
  @Input() resetSaveError: string | null = null;
  @Input() resetSuccess = false;

  @Output() submit = new EventEmitter<LoginSubmitPayload>();
  @Output() recover = new EventEmitter<string>();
  @Output() resetPassword = new EventEmitter<string>();
  /** Fires when the user tabs/clicks out of the email field with a valid-looking value.
   *  Parent può usarlo per prefetch (es. cliente: getCliente Firestore) così al submit
   *  la query è già pronta. */
  @Output() userBlur = new EventEmitter<string>();
  /** Codice magic-link inserito manualmente dall'utente in PWA (quando il
   *  deep-link da email non funziona, es. iOS standalone). Il parent deve
   *  chiamare `/users/verifyCode` e gestire la sessione come per il token. */
  @Output() codeSubmit = new EventEmitter<string>();

  /** Feedback dal parent: codice accettato/rifiutato. */
  @Input() codeLoading = false;
  @Input() codeError: string | null = null;

  username = "";
  password = "";
  isPasswordVisible = false;
  formError = false;

  // hoveredBubble/bubbleTexts sono ora in LoginHeroComponent (shared) — placeholder declarato per compat col template storico in fase di migrazione.
  private _unused_bubbles?: Record<string, string> = {
    heart: "Lo straordinario viaggio che ti allinea alla tua impresa.",
    head: "La strategia da cui prende forma la vera espansione.",
    hands: "\u201CTu e le tue Persone\u201D: la concretezza dell\u2019azione.",
    results: "\u00C8 il cerchio che si chiude, l\u2019auto-evoluzione della tua impresa.",
  };

  recoverView = false;
  recoverEmail = "";
  recoverLoading = false;

  // ── Code-entry view (PWA standalone fallback) ────────────────────────────
  codeView = false;
  enteredCode = "";

  /** True se la pagina è aperta in una PWA installata (standalone).
   *  Include iOS (`navigator.standalone`) e Android/Desktop (`display-mode:
   *  standalone`). Il pulsante "Ho un codice" è visibile SOLO in PWA,
   *  perché in browser normale il deep-link del magic-link funziona. */
  get isPWA(): boolean {
    if (typeof window === "undefined") return false;
    const standaloneMatch = window.matchMedia?.("(display-mode: standalone)")?.matches ?? false;
    const iosStandalone = (window.navigator as any)?.standalone === true;
    return !!(standaloneMatch || iosStandalone);
  }

  newPassword = "";
  confirmPassword = "";
  resetFormError: string | null = null;

  private captchaToken: string | null = null;
  private captchaPromise: Promise<string | null> | null = null;
  private destroyed = false;

  canInstall$!: Observable<boolean>;
  @ViewChild("iosInstallTpl", { static: true }) iosInstallTpl!: TemplateRef<void>;
  @ViewChild("desktopInstallTpl", { static: true }) desktopInstallTpl!: TemplateRef<void>;

  constructor(
    private captcha: CaptchaService,
    private pwaInstall: PwaInstallService,
    private modal: NzModalService,
  ) {}

  ngOnInit(): void {
    if (this.startInRecover) {
      this.recoverView = true;
    }
    this.canInstall$ = this.pwaInstall.canInstall$();
    this.prefetchCaptcha();
  }

  /** Riferimento alla modale di istruzioni attualmente aperta (se presente).
   *  Garantisce che un solo dialog di install sia visibile per volta: click
   *  ripetuti sul pulsante portano in focus quella esistente invece di
   *  aprirne una nuova sovrapposta. */
  private installModalRef: NzModalRef | null = null;

  async onInstallClick(): Promise<void> {
    if (this.installModalRef) {
      // Dialog già aperta: non aprirne una seconda. Riporta focus all'esistente.
      this.installModalRef.triggerOk();
      return;
    }
    const result = await this.pwaInstall.promptInstall();
    if (result === "ios-manual") {
      this.openInstructions(this.iosInstallTpl);
    } else if (result === "desktop-manual") {
      this.openInstructions(this.desktopInstallTpl);
    }
  }

  private openInstructions(tpl: TemplateRef<void>): void {
    if (this.installModalRef) return;
    this.installModalRef = this.modal.create({
      nzTitle: undefined,
      nzContent: tpl,
      nzFooter: null,
      nzClosable: true,
      nzMaskClosable: true,
      nzCentered: true,
      nzWidth: 520,
      nzClassName: "install-modal",
    });
    this.installModalRef.afterClose.pipe(take(1)).subscribe(() => {
      this.installModalRef = null;
    });
  }

  // ── Countdown codice OTP (15 min dal momento dell'invio email) ───────────
  /** Timestamp (ms) di scadenza codice: 15 min da `success=true`. */
  private codeExpiresAt: number | null = null;
  /** Display formattato "MM:SS" per il badge timer a destra dell'input. */
  codeCountdown = "15:00";
  /** True quando mancano meno di 60s → stile "urgente" (pulsa rosso). */
  codeCountdownCritical = false;
  /** True quando il timer è a 0 → codice scaduto, disable input. */
  codeCountdownExpired = false;
  private countdownHandle: any = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['recoverSuccess'] || changes['recoverError']) {
      this.recoverLoading = false;
    }
    if (changes['success']) {
      const nowSuccess = !!this.success;
      if (nowSuccess) {
        this.startCodeCountdown();
      } else {
        this.stopCodeCountdown();
      }
    }
  }

  private startCodeCountdown(): void {
    this.stopCodeCountdown();
    this.codeExpiresAt = Date.now() + 15 * 60 * 1000;
    this.codeCountdownExpired = false;
    this.updateCodeCountdown();
    this.countdownHandle = setInterval(() => this.updateCodeCountdown(), 1000);
  }

  private updateCodeCountdown(): void {
    if (!this.codeExpiresAt) return;
    const remainMs = Math.max(0, this.codeExpiresAt - Date.now());
    const totalSec = Math.floor(remainMs / 1000);
    const mm = Math.floor(totalSec / 60);
    const ss = totalSec % 60;
    this.codeCountdown = `${mm.toString().padStart(2, "0")}:${ss.toString().padStart(2, "0")}`;
    this.codeCountdownCritical = totalSec > 0 && totalSec <= 60;
    if (remainMs <= 0) {
      this.codeCountdownExpired = true;
      this.stopCodeCountdown();
    }
  }

  private stopCodeCountdown(): void {
    if (this.countdownHandle) {
      clearInterval(this.countdownHandle);
      this.countdownHandle = null;
    }
  }

  /**
   * Su mobile (<= 768px) alcuni browser / webview danno focus automatico
   * al primo `<input>` della pagina, aprendo la tastiera e causando scroll
   * indesiderato. L'utente ha chiesto esplicitamente di poter cliccare il
   * campo manualmente — quindi blurro qualsiasi elemento focalizzato su
   * mobile subito dopo la view è pronta. Su desktop lasciamo il comportamento
   * naturale (nessun autofocus esplicito nel template).
   */
  ngAfterViewInit(): void {
    if (typeof window === "undefined") return;
    const isMobile = window.matchMedia?.("(max-width: 768px)").matches;
    if (!isMobile) return;
    setTimeout(() => {
      const active = document.activeElement as HTMLElement | null;
      if (active && typeof active.blur === "function" && active !== document.body) {
        active.blur();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopCodeCountdown();
    // La modale vive nell'overlay globale: chiuderla se la shell viene distrutta.
    this.installModalRef?.destroy();
    this.installModalRef = null;
  }

  togglePasswordVisibility(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  /** Lightweight email shape check — non replacing server validation, just
   *  a gate to avoid firing prefetch on garbage input. */
  private looksLikeEmail(v: string): boolean {
    const s = (v || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
  }

  onUsernameBlur(): void {
    const v = (this.username || "").trim().toLowerCase();
    if (this.looksLikeEmail(v)) {
      this.userBlur.emit(v);
    }
  }

  showRecoverView(): void {
    this.recoverEmail = this.username;
    this.recoverView = true;
  }

  backToLogin(): void {
    this.recoverView = false;
    this.recoverLoading = false;
    this.recoverSuccess = false;
    this.recoverError = null;
    this.codeView = false;
    this.enteredCode = "";
    this.codeError = null;
  }

  showCodeView(): void {
    this.codeView = true;
    this.enteredCode = "";
    this.codeError = null;
  }

  onCodeInputSubmit(): void {
    const code = (this.enteredCode || "").trim().toUpperCase();
    if (!code || code.length < 4) {
      this.codeError = "Inserisci il codice ricevuto via email.";
      return;
    }
    this.codeError = null;
    this.codeSubmit.emit(code);
  }

  onRecoverSubmit(): void {
    if (!this.recoverEmail) return;
    this.recoverLoading = true;
    this.recover.emit(this.recoverEmail.trim().toLowerCase());
  }

  onResetSubmit(): void {
    this.resetFormError = null;
    this.resetSaveError = null;
    if (!this.newPassword || this.newPassword.length < 8) {
      this.resetFormError = "La password deve contenere almeno 8 caratteri.";
      return;
    }
    if (this.newPassword !== this.confirmPassword) {
      this.resetFormError = "Le password non coincidono.";
      return;
    }
    this.resetPassword.emit(this.newPassword);
  }

  /**
   * Called by the form CTA + Enter key. Validates inputs locally, then
   * awaits the captcha token (with a 3s timeout) before emitting submit.
   */
  async onFormSubmit(): Promise<void> {
    this.formError = false;

    if (!this.username.trim()) {
      this.formError = true;
      return;
    }
    if (this.mode === "admin" && !this.password) {
      this.formError = true;
      return;
    }

    const token = await this.awaitCaptchaWithTimeout(3000);

    // Turnstile tokens are single-use - burn it so the next attempt gets a fresh one.
    this.captchaToken = null;
    this.captchaPromise = null;
    this.prefetchCaptcha();

    this.submit.emit({
      user: this.username.trim().toLowerCase(),
      pwd: this.mode === "admin" ? this.password : undefined,
      captchaToken: token,
    });
  }

  private prefetchCaptcha(): void {
    if (this.destroyed) return;
    this.captchaPromise = this.captcha
      .getToken(this.mode === "admin" ? "login" : "link-access")
      .then((token) => {
        this.captchaToken = token;
        return token;
      })
      .catch(() => null);
  }

  private awaitCaptchaWithTimeout(ms: number): Promise<string | null> {
    if (this.captchaToken) return Promise.resolve(this.captchaToken);
    if (!this.captchaPromise) return Promise.resolve(null);

    const timeout = new Promise<string | null>((resolve) =>
      setTimeout(() => resolve(null), ms)
    );
    return Promise.race([this.captchaPromise, timeout]);
  }
}
